import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { usePromotionHistory, type PromotionHistoryRow } from '@/hooks/usePromotionHistory';
import { revertComponentPromotion } from '@/lib/dvi/componentPromotionEngine';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Undo2, ArrowUpRight, ArrowDownRight, AlertOctagon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

interface Props {
  shopId: string | null;
  canRevert?: boolean;
}

const actionMeta = {
  promote: { label: 'Promoted', icon: ArrowUpRight, className: 'text-primary' },
  demote: { label: 'Demoted', icon: ArrowDownRight, className: 'text-muted-foreground' },
  hard_demote: { label: 'Hard demote', icon: AlertOctagon, className: 'text-destructive' },
  manual_revert: { label: 'Reverted', icon: Undo2, className: 'text-muted-foreground' },
} as const;

export function PromotionHistoryPanel({ shopId, canRevert = true }: Props) {
  const { data: rows = [], isLoading } = usePromotionHistory(shopId, { limit: 30 });
  const { toast } = useToast();
  const qc = useQueryClient();
  const [reverting, setReverting] = useState<string | null>(null);

  const handleRevert = async (row: PromotionHistoryRow) => {
    setReverting(row.id);
    try {
      // Revert by state ID — find latest state row for this component
      const { data: stateRow } = await (await import('@/integrations/supabase/client')).supabase
        .from('dvi_component_promotion_state' as any)
        .select('id')
        .eq('shop_id', row.shop_id)
        .eq('component', row.component)
        .eq('condition_type', row.condition_type)
        .maybeSingle();

      if (!stateRow) throw new Error('Promotion state not found');

      const result = await revertComponentPromotion((stateRow as any).id, 'manager_revert');
      if (!result.ok) throw new Error(result.error ?? 'Revert failed');
      toast({ title: 'Reverted', description: `${row.component} returned to ${row.from_stage}` });
      qc.invalidateQueries({ queryKey: ['dvi-promotion-history'] });
      qc.invalidateQueries({ queryKey: ['dvi-component-promotion-state'] });
    } catch (e: any) {
      toast({ title: 'Revert failed', description: e.message, variant: 'destructive' });
    } finally {
      setReverting(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Promotion History</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">No promotions yet — engine evaluates every 15 minutes.</div>
        ) : (
          <ScrollArea className="h-80">
            <div className="space-y-3 pr-4">
              {rows.map((row) => {
                const meta = actionMeta[row.action] ?? actionMeta.promote;
                const Icon = meta.icon;
                const snap = row.metrics_snapshot ?? {};
                const wc = snap.weighted_contributions ?? {};
                return (
                  <div key={row.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2">
                        <Icon className={`h-4 w-4 mt-0.5 ${meta.className}`} />
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {row.component}
                            <span className="text-muted-foreground font-normal"> · {row.condition_type}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {meta.label}: {row.from_stage ?? '—'} → {row.to_stage}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {row.triggered_by}
                        </Badge>
                        {canRevert && row.action !== 'manual_revert' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={reverting === row.id}
                            onClick={() => handleRevert(row)}
                          >
                            <Undo2 className="h-3 w-3 mr-1" />
                            Revert
                          </Button>
                        )}
                      </div>
                    </div>

                    {Object.keys(wc).length > 0 && (
                      <ContributionBar contributions={wc} />
                    )}

                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>Promo {fmt(snap.promotion_score)}</span>
                      <span>Reg {fmt(snap.regression_score)}</span>
                      <span>{formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

function fmt(n: number | undefined) {
  return typeof n === 'number' ? n.toFixed(2) : '—';
}

const SIGNAL_COLORS: Record<string, string> = {
  acceptance_score: 'bg-primary',
  mapping_score: 'bg-accent',
  stability_score: 'bg-secondary',
  cross_tech_consistency: 'bg-muted-foreground',
  cross_ticket_consistency: 'bg-muted',
  sample_sufficiency: 'bg-primary/60',
  velocity_score: 'bg-accent/60',
};

function ContributionBar({ contributions }: { contributions: Record<string, number> }) {
  const total = Object.values(contributions).reduce((a, b) => a + (b || 0), 0) || 1;
  return (
    <div className="space-y-1">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
        {Object.entries(contributions).map(([k, v]) => {
          const pct = ((v || 0) / total) * 100;
          if (pct < 0.5) return null;
          return (
            <div
              key={k}
              className={SIGNAL_COLORS[k] ?? 'bg-muted-foreground'}
              style={{ width: `${pct}%` }}
              title={`${k}: ${(v as number).toFixed(3)}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
        {Object.entries(contributions)
          .filter(([, v]) => (v || 0) > 0.001)
          .sort((a, b) => (b[1] || 0) - (a[1] || 0))
          .slice(0, 3)
          .map(([k, v]) => (
            <span key={k}>
              {prettyKey(k)} {(v as number).toFixed(2)}
            </span>
          ))}
      </div>
    </div>
  );
}

function prettyKey(k: string) {
  return k.replace(/_score|_consistency|_sufficiency/g, '').replace(/_/g, ' ');
}
